import React, { useState, useRef } from 'react';
import '../../App.css';
import { toast, ToastContainer } from 'react-toastify';
import { uploadScormFile } from '../../apis/scormUpload';
import { useDispatch } from 'react-redux';
import { addScormFile } from '../../store/slices/scormFilesSlice';
import Loader from '../loader/Loader';

const ScormUpload = () => {
    const [file, setFile] = useState(null);
    const [progress, setProgress] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [uploadStartTime, setUploadStartTime] = useState(null);
    const [uploadSpeed, setUploadSpeed] = useState(null);
    const [lastLoaded, setLastLoaded] = useState(0);
    const fileInputRef = useRef();
    const dispatch = useDispatch();

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
        setProgress(0);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            setFile(e.dataTransfer.files[0]);
            setProgress(0);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleBrowseClick = () => {
        fileInputRef.current.click();
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!file) {
            toast.error('Please select a SCORM .zip file to upload.');
            return;
        }
        setUploading(true);
        setProgress(0);
        setUploadSpeed(null);
        setUploadStartTime(Date.now());
        setLastLoaded(0);
        try {
            const uploadedFile = await uploadScormFile(file, (event) => {
                if (event.total) {
                    setProgress(Math.round((event.loaded * 100) / event.total));
                }
                // Calculate speed
                const now = Date.now();
                if (uploadStartTime) {
                    const elapsedSec = (now - uploadStartTime) / 1000;
                    const bytesUploaded = event.loaded;
                    if (elapsedSec > 0) {
                        const speed = bytesUploaded / elapsedSec; // bytes per second
                        setUploadSpeed(speed);
                    }
                }
                setLastLoaded(event.loaded);
            });
            setUploading(false);
            setProgress(100);
            setUploadSpeed(null);
            toast.success('Upload successful!');
            if (uploadedFile) {
                dispatch(addScormFile(uploadedFile.scorm_file));
            }
        } catch (err) {
            setUploading(false);
            setProgress(0);
            setUploadSpeed(null);
            // Handle error with code
            const errorMessage = err?.data?.error || (typeof err === 'string' ? err : 'Upload failed. Please try again.');
            toast.error(errorMessage);
          
        }
    };

    const formatSize = (size) => {
        if (!size) return '';
        if (size > 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
        if (size > 1024) return `${(size / 1024).toFixed(1)} KB`;
        return `${size} B`;
    };

    // Helper to format speed
    const formatSpeed = (speed) => {
        if (!speed) return '';
        if (speed > 1024 * 1024) {
            return `${(speed / (1024 * 1024)).toFixed(2)} MB/s`;
        } else if (speed > 1024) {
            return `${(speed / 1024).toFixed(2)} KB/s`;
        } else {
            return `${speed.toFixed(2)} B/s`;
        }
    };

    return (
        <div className="d-flex justify-content-center align-items-center py-5 my-3 align-middle">
            <div className="card shadow border-0 bg-light-gradient w-100 position-relative" style={{ maxWidth: 900 }}>
                <div className="card-body p-5">
                    <Loader isLoading={uploading} text={'Uploading...'} />
                    <div className="row g-0 align-items-stretch">
                        <div className="col-md-7 pe-md-4 border-end border-1 border-light-subtle">
                            <h3 className="mb-4 text-center fw-semibold text-dark">File upload</h3>
                            <div
                                className="d-flex flex-column align-items-center justify-content-center mb-4 w-100 py-4 px-2 bg-light  border-2 border-dashed rounded-4 position-relative"
                                style={{ cursor: uploading ? 'not-allowed' : 'pointer' }}
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onClick={handleBrowseClick}
                            >
                                <input
                                    type="file"
                                    className="d-none"
                                    accept=".zip"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    disabled={uploading}
                                />
                                <div className="mb-2 text-secondary" style={{ fontSize: 48 }}>
                                    <svg width="48" height="48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <rect width="48" height="48" rx="12" fill="#e9ecf3" />
                                        <path
                                            d="M24 14v14m0 0l-5-5m5 5l5-5"
                                            stroke="#7a88a9"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <rect x="14" y="32" width="20" height="2" rx="1" fill="#b0b8c9" />
                                    </svg>
                                </div>
                                <div className="text-secondary fw-medium fs-6">
                                    Drag and drop or{' '}
                                    <span className="text-primary text-decoration-underline" style={{ cursor: 'pointer' }}>
                                        browse
                                    </span>{' '}
                                    your files
                                </div>
                            </div>
                            {file && (
                                <div className="d-flex align-items-center mb-2 gap-3">
                                    <div className="text-secondary" style={{ fontSize: 32 }}>
                                        <svg width="32" height="32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <rect width="32" height="32" rx="6" fill="#e9ecf3" />
                                            <text
                                                x="16"
                                                y="22"
                                                textAnchor="middle"
                                                fontSize="10"
                                                fill="#7a88a9"
                                                fontFamily="Arial"
                                                fontWeight="bold"
                                            >
                                                ZIP
                                            </text>
                                        </svg>
                                    </div>
                                    <div className="flex-grow-1">
                                        <div className="fw-semibold text-dark">{file.name}</div>
                                        <div className="small text-secondary">{formatSize(file.size)}</div>
                                    </div>
                                </div>
                            )}
                            {file && (
                                <div className="mb-3">
                                    <div className="d-flex align-items-center gap-2">
                                        <div
                                            className="flex-grow-1 bg-light rounded-pill overflow-hidden"
                                            style={{ height: 7 }}
                                        >
                                            <div
                                                className="bg-primary rounded-pill"
                                                style={{
                                                    width: `${progress}%`,
                                                    height: 7,
                                                    transition: 'width 0.3s',
                                                }}
                                            />
                                        </div>
                                        <div className="small text-primary text-end" style={{ minWidth: 60 }}>
                                            {uploading ? `Uploading... ${progress}%` : progress === 100 ? 'Done' : null}
                                        </div>
                                    </div>
                                    {uploading && (
                                        <div className="small text-secondary mt-1 text-end" style={{ minWidth: 60 }}>
                                            Speed: {formatSpeed(uploadSpeed)}
                                        </div>
                                    )}
                                </div>
                            )}
                            <button
                                className="btn w-100 rounded-pill fw-semibold fs-5 py-2 mt-3 mb-2 btn-primary-gradient"
                                style={{
                                    letterSpacing: 1,
                                    boxShadow: uploading ? 'none' : '0 2px 8px rgba(26,156,255,0.08)',
                                    opacity: uploading ? 0.7 : 1,
                                }}
                                onClick={handleUpload}
                                disabled={uploading || !file}
                            >
                                DONE
                            </button>
                        </div>
                        <div className="col-md-5 d-flex flex-column justify-content-center ps-md-4 pt-4 pt-md-0">
                            <div className="fs-5 fw-semibold text-primary mb-3">
                                Requirements <i className="bi bi-info-circle-fill text-primary"></i>
                            </div>
                            <ul className="text-secondary fs-6 ps-3 mb-0">
                                <li className="my-3">
                                    File must be less than <b>500MB</b>
                                </li>
                                <li className="my-3">
                                    It must be a <b>SCORM .zip</b> file
                                </li>
                                <li className="my-3">
                                    It must be a valid SCORM package containing a <b>manifest file</b>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <ToastContainer />
            </div>
        </div>
    );
};

export default ScormUpload;
